/**
 * Ring PRO charging case variants. 2 options (gold, black), each with size variants.
 * Variant IDs and URLs are sourced from ringProVariantIds.ts for easy maintenance.
 * In staging, uses Ring Air Travel Charger staging variants so the full flow can be tested.
 */
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { TravelChargerShopifyMap } from 'content/products/accessories/TravelChargerShopifyMap';
import {
  isRingProSkuDisabled,
  RingProChargerCaseColor,
  RingProChargerCaseVariantType,
  RingSizes,
} from './constants';
import { CHARGER_CASE_DATA_EXPANDED } from './ringProVariantIds';

const SIZES = [
  RingSizes.FIVE,
  RingSizes.SIX,
  RingSizes.SEVEN,
  RingSizes.EIGHT,
  RingSizes.NINE,
  RingSizes.TEN,
  RingSizes.ELEVEN,
  RingSizes.TWELVE,
  RingSizes.THIRTEEN,
  RingSizes.FOURTEEN,
  RingSizes.OPEN,
].filter((size) => !isRingProSkuDisabled({ size }));

/** Ring PRO charging case is free; all stores 0. */
const PLACEHOLDER_PRICE: Partial<Record<ShopifyStore, number>> = {
  [ShopifyStore.US]: 0,
  [ShopifyStore.ROW]: 0,
  [ShopifyStore.EU]: 0,
  [ShopifyStore.GB]: 0,
  [ShopifyStore.IN]: 0,
  [ShopifyStore.AE]: 0,
  [ShopifyStore.AU]: 0,
  [ShopifyStore.CA]: 0,
  [ShopifyStore.SA]: 0,
  [ShopifyStore.MX]: 0,
  [ShopifyStore.ZA]: 0,
  [ShopifyStore.JP]: 0,
};

function buildChargerCaseMap(
  color: RingProChargerCaseColor,
): Partial<Record<RingSizes, RingProChargerCaseVariantType>> {
  const map: Partial<Record<RingSizes, RingProChargerCaseVariantType>> = {};

  for (const size of SIZES) {
    const sizeKey = size === RingSizes.OPEN ? 99 : (size as number);
    const data = CHARGER_CASE_DATA_EXPANDED[color]?.[sizeKey] ?? {};

    const variantId: Partial<Record<ShopifyStore, string>> = {};
    const checkoutUrl: Partial<Record<ShopifyStore, string>> = {};

    for (const [store, entry] of Object.entries(data)) {
      if (entry) {
        variantId[store as ShopifyStore] = entry.id;
        checkoutUrl[store as ShopifyStore] = entry.url;
      }
    }

    map[size] = {
      variantId,
      checkoutUrl,
      price: { ...PLACEHOLDER_PRICE },
    };
  }
  return map;
}

/** Staging: use Ring Air Travel Charger variants so the full Ring Pro flow can be tested. */
function buildChargerCaseMapStaging(): Partial<
  Record<RingSizes, RingProChargerCaseVariantType>
> {
  const map: Partial<Record<RingSizes, RingProChargerCaseVariantType>> = {};

  for (const size of SIZES) {
    const variantId: Partial<Record<ShopifyStore, string>> = {};
    const checkoutUrl: Partial<Record<ShopifyStore, string>> = {};

    for (const store of Object.values(ShopifyStore)) {
      const variantConfig = TravelChargerShopifyMap[store]?.[size] as
        | { shopifyVariantId?: string; checkoutUrl?: string }
        | undefined;
      if (variantConfig?.shopifyVariantId) {
        variantId[store] = variantConfig.shopifyVariantId;
        checkoutUrl[store] = variantConfig.checkoutUrl ?? '';
      }
    }

    map[size] = {
      variantId,
      checkoutUrl,
      price: { ...PLACEHOLDER_PRICE },
    };
  }
  return map;
}

const isProduction = process.env.NEXT_PUBLIC_ENVIRONMENT === 'PRODUCTION';

export const RingProChargerCaseVariants: Record<
  RingProChargerCaseColor,
  Partial<Record<RingSizes, RingProChargerCaseVariantType>>
> = isProduction
  ? {
      [RingProChargerCaseColor.GOLD]: buildChargerCaseMap(
        RingProChargerCaseColor.GOLD,
      ),
      [RingProChargerCaseColor.BLACK]: buildChargerCaseMap(
        RingProChargerCaseColor.BLACK,
      ),
    }
  : (() => {
      const stagingMap = buildChargerCaseMapStaging();
      return {
        [RingProChargerCaseColor.GOLD]: stagingMap,
        [RingProChargerCaseColor.BLACK]: stagingMap,
      };
    })();
